import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Cookie, Settings, Shield, Eye, Clock, ToggleLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PageSeo } from '@/components/seo/PageSeo';
import { useNavigate } from 'react-router-dom';

const CookiePolicyPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const currentLang = i18n.language;

  const content = {
    de: {
      title: "COOKIE-RICHTLINIE",
      effectiveDate: "Gültig ab: 10 Januar 2026",
      intro: {
        title: "Was sind Cookies?",
        text: `Cookies sind kleine Textdateien, die auf Ihrem Gerät gespeichert werden, wenn Sie unsere Website besuchen. Sie helfen uns, Ihre Einstellungen zu speichern, Ihre Erfahrung zu verbessern und unsere Dienste zu optimieren.`
      },
      section1: {
        title: "1. Welche Cookies verwenden wir?",
        items: [
          {
            name: "Notwendige Cookies",
            desc: "Diese Cookies sind für das Funktionieren der Website unerlässlich. Sie ermöglichen grundlegende Funktionen wie Seitennavigation und Zugriff auf sichere Bereiche."
          },
          {
            name: "Funktionale Cookies",
            desc: "Diese Cookies ermöglichen erweiterte Funktionalität und Personalisierung, wie z.B. das Speichern Ihrer Spracheinstellungen."
          },
          {
            name: "Analyse-Cookies",
            desc: "Diese Cookies helfen uns zu verstehen, wie Besucher mit unserer Website interagieren, indem sie Informationen anonym sammeln."
          },
          {
            name: "Marketing-Cookies",
            desc: "Diese Cookies werden verwendet, um Werbung relevanter für Sie zu gestalten. Sie helfen uns, die Wirksamkeit unserer Werbekampagnen zu messen."
          }
        ]
      },
      section2: {
        title: "2. Wie lange bleiben Cookies gespeichert?",
        text: `Die Speicherdauer variiert je nach Cookie-Typ:
• Sitzungs-Cookies: Werden gelöscht, wenn Sie Ihren Browser schließen
• Permanente Cookies: Bleiben für einen festgelegten Zeitraum (bis zu 2 Jahre) gespeichert`
      },
      section3: {
        title: "3. Wie können Sie Cookies verwalten?",
        text: `Sie können Ihre Cookie-Einstellungen jederzeit über unser Cookie-Banner oder in Ihren Browsereinstellungen ändern. Bitte beachten Sie, dass das Blockieren einiger Cookies die Funktionalität unserer Website beeinträchtigen kann.`
      },
      section4: {
        title: "4. Cookies von Drittanbietern",
        text: `Wir können auch Cookies von Drittanbietern verwenden, wie z.B. Google Analytics, um den Website-Verkehr zu analysieren, und Zahlungsanbieter für sichere Transaktionen.`
      },
      section5: {
        title: "5. Änderungen an dieser Richtlinie",
        text: `Wir können diese Cookie-Richtlinie von Zeit zu Zeit aktualisieren. Wir empfehlen Ihnen, diese Seite regelmäßig zu besuchen, um über Änderungen informiert zu bleiben.`
      },
      contact: {
        title: "Kontakt",
        text: `Bei Fragen zu unserer Cookie-Richtlinie kontaktieren Sie uns bitte unter:
E-Mail: datenschutz@turkishemarket.com`
      }
    },
    en: {
      title: "COOKIE POLICY",
      effectiveDate: "Effective Date: January 10, 2026",
      intro: {
        title: "What are Cookies?",
        text: `Cookies are small text files stored on your device when you visit our website. They help us remember your preferences, improve your experience, and optimize our services.`
      },
      section1: {
        title: "1. What Cookies Do We Use?",
        items: [
          {
            name: "Necessary Cookies",
            desc: "These cookies are essential for the website to function. They enable basic features like page navigation and access to secure areas."
          },
          {
            name: "Functional Cookies",
            desc: "These cookies enable enhanced functionality and personalization, such as remembering your language preferences."
          },
          {
            name: "Analytics Cookies",
            desc: "These cookies help us understand how visitors interact with our website by collecting information anonymously."
          },
          {
            name: "Marketing Cookies",
            desc: "These cookies are used to make advertising more relevant to you. They help us measure the effectiveness of our advertising campaigns."
          }
        ]
      },
      section2: {
        title: "2. How Long Do Cookies Stay?",
        text: `Storage duration varies by cookie type:
• Session Cookies: Deleted when you close your browser
• Persistent Cookies: Stored for a set period (up to 2 years)`
      },
      section3: {
        title: "3. How Can You Manage Cookies?",
        text: `You can change your cookie preferences at any time through our cookie banner or in your browser settings. Please note that blocking some cookies may affect the functionality of our website.`
      },
      section4: {
        title: "4. Third-Party Cookies",
        text: `We may also use third-party cookies, such as Google Analytics to analyze website traffic, and payment providers for secure transactions.`
      },
      section5: {
        title: "5. Changes to This Policy",
        text: `We may update this Cookie Policy from time to time. We recommend visiting this page regularly to stay informed about changes.`
      },
      contact: {
        title: "Contact",
        text: `For questions about our Cookie Policy, please contact us at:
Email: privacy@turkishemarket.com`
      }
    },
    tr: {
      title: "ÇEREZ POLİTİKASI",
      effectiveDate: "Yürürlük Tarihi: 10 Ocak 2026",
      intro: {
        title: "Çerezler Nedir?",
        text: `Çerezler, web sitemizi ziyaret ettiğinizde cihazınızda depolanan küçük metin dosyalarıdır. Tercihlerinizi hatırlamamıza, deneyiminizi iyileştirmemize ve hizmetlerimizi optimize etmemize yardımcı olurlar.`
      },
      section1: {
        title: "1. Hangi Çerezleri Kullanıyoruz?",
        items: [
          {
            name: "Zorunlu Çerezler",
            desc: "Bu çerezler web sitesinin çalışması için gereklidir. Sayfa navigasyonu ve güvenli alanlara erişim gibi temel özellikleri etkinleştirirler."
          },
          {
            name: "İşlevsel Çerezler",
            desc: "Bu çerezler, dil tercihlerinizi hatırlama gibi gelişmiş işlevsellik ve kişiselleştirme sağlar."
          },
          {
            name: "Analitik Çerezler",
            desc: "Bu çerezler, ziyaretçilerin web sitemizle nasıl etkileşime girdiğini anonim olarak bilgi toplayarak anlamamıza yardımcı olur."
          },
          {
            name: "Pazarlama Çerezleri",
            desc: "Bu çerezler, reklamları sizin için daha alakalı hale getirmek için kullanılır. Reklam kampanyalarımızın etkinliğini ölçmemize yardımcı olurlar."
          }
        ]
      },
      section2: {
        title: "2. Çerezler Ne Kadar Süre Saklanır?",
        text: `Saklama süresi çerez türüne göre değişir:
• Oturum Çerezleri: Tarayıcınızı kapattığınızda silinir
• Kalıcı Çerezler: Belirli bir süre (2 yıla kadar) saklanır`
      },
      section3: {
        title: "3. Çerezleri Nasıl Yönetebilirsiniz?",
        text: `Çerez tercihlerinizi istediğiniz zaman çerez bannerımız veya tarayıcı ayarlarınız aracılığıyla değiştirebilirsiniz. Lütfen bazı çerezleri engellemenin web sitemizin işlevselliğini etkileyebileceğini unutmayın.`
      },
      section4: {
        title: "4. Üçüncü Taraf Çerezleri",
        text: `Web sitesi trafiğini analiz etmek için Google Analytics ve güvenli işlemler için ödeme sağlayıcıları gibi üçüncü taraf çerezlerini de kullanabiliriz.`
      },
      section5: {
        title: "5. Bu Politikadaki Değişiklikler",
        text: `Bu Çerez Politikasını zaman zaman güncelleyebiliriz. Değişikliklerden haberdar olmak için bu sayfayı düzenli olarak ziyaret etmenizi öneririz.`
      },
      contact: {
        title: "İletişim",
        text: `Çerez Politikamız hakkında sorularınız için lütfen bizimle iletişime geçin:
E-posta: gizlilik@turkishemarket.com`
      }
    }
  };

  const c = content[currentLang] || content['en'];

  return (
    <div className="min-h-screen bg-background" data-testid="cookie-policy-page">
      <PageSeo title={c.title} description={c.intro?.text} segments={['cookies']} />
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Button 
          variant="ghost" 
          className="mb-6"
          onClick={() => navigate(-1)}
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('common.back')}
        </Button>

        <div className="bg-card rounded-lg shadow-sm border p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-primary/10 rounded-full">
              <Cookie className="w-8 h-8 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">{c.title}</h1>
              <p className="text-muted-foreground text-sm">{c.effectiveDate}</p>
            </div>
          </div>

          <div className="prose prose-gray max-w-none space-y-8">
            {/* Introduction */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <Eye className="w-5 h-5 text-primary" />
                <h2 className="text-xl font-semibold m-0">{c.intro.title}</h2>
              </div>
              <p className="text-muted-foreground">{c.intro.text}</p>
            </section>

            {/* Section 1 - Cookie Types */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <Settings className="w-5 h-5 text-primary" />
                <h2 className="text-xl font-semibold m-0">{c.section1.title}</h2>
              </div>
              <div className="grid gap-4 mt-4">
                {c.section1.items.map((item, idx) => (
                  <div key={idx} className="bg-muted/30 p-4 rounded-lg">
                    <h3 className="font-semibold text-foreground mb-2">{item.name}</h3>
                    <p className="text-muted-foreground text-sm">{item.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Section 2 */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <Clock className="w-5 h-5 text-primary" />
                <h2 className="text-xl font-semibold m-0">{c.section2.title}</h2>
              </div>
              <p className="text-muted-foreground whitespace-pre-line">{c.section2.text}</p>
            </section>

            {/* Section 3 */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <ToggleLeft className="w-5 h-5 text-primary" />
                <h2 className="text-xl font-semibold m-0">{c.section3.title}</h2>
              </div>
              <p className="text-muted-foreground">{c.section3.text}</p>
            </section>

            {/* Section 4 */}
            <section>
              <h2 className="text-xl font-semibold">{c.section4.title}</h2>
              <p className="text-muted-foreground">{c.section4.text}</p>
            </section>

            {/* Section 5 */}
            <section>
              <h2 className="text-xl font-semibold">{c.section5.title}</h2>
              <p className="text-muted-foreground">{c.section5.text}</p>
            </section>

            {/* Contact */}
            <section className="bg-muted/50 p-6 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <Shield className="w-5 h-5 text-primary" />
                <h2 className="text-xl font-semibold m-0">{c.contact.title}</h2>
              </div>
              <p className="text-muted-foreground whitespace-pre-line">{c.contact.text}</p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicyPage;
